// AIDEV-NOTE: typed-navigate; the one sanctioned wrapper around react-router's useNavigate.
// Raw `useNavigate` is ESLint-banned everywhere except this folder. The `state` argument is
// constrained to `JsonSafe<S>` so a non-serializable value (e.g. Temporal.PlainDate) crossing
// the Structured Clone Algorithm boundary fails to type-check instead of silently corrupting
// to `{}` at runtime (issue #408). The wrapper is a *faithful superset* of NavigateFunction —
// including the numeric `navigate(delta)` form — so the codebase-wide migration is mechanical
// (NotFound.tsx's `navigate(-1)` keeps working unchanged).

import { useCallback } from 'react'
import {
  useNavigate,
  useLocation,
  type To,
  type NavigateOptions,
} from 'react-router-dom'
import type { JsonSafe } from '../serialization/json-safe'

interface TypedNavigateFunction {
  (delta: number): void
  // AIDEV-NOTE: jsonsafe-constraint; `state?: S & JsonSafe<S>` constrains the passed value to be
  // serializable without the circular `<S extends JsonSafe<S>>` constraint (TS2313). S is inferred
  // from the naked `S`; a non-serializable field (e.g. Temporal.PlainDate) becomes `never` in
  // JsonSafe<S>, so the intersection makes that field `never` and the call fails to type-check.
  <S>(
    to: To,
    options?: Omit<NavigateOptions, 'state'> & { state?: S & JsonSafe<S> }
  ): void
}

export function useTypedNavigate(): TypedNavigateFunction {
  const navigate = useNavigate()
  return useCallback(
    ((to: To | number, options?: NavigateOptions) => {
      // Faithful pass-through: forward exactly the args react-router would receive, so a bare
      // `navigate('/x')` doesn't become `navigate('/x', undefined)` (keeps call-arg assertions
      // and react-router's own overload resolution identical to the un-wrapped hook).
      if (typeof to === 'number') return navigate(to)
      if (options === undefined) return navigate(to)
      return navigate(to, options)
    }) as TypedNavigateFunction,
    [navigate]
  )
}

export function useTypedLocationState<T>(): T | null {
  return (useLocation().state as T | null) ?? null
}
